import {
  createContext,
  useContext,
  useEffect,
  useState,
  type ReactNode,
} from "react";
import type { Session, User } from "@supabase/supabase-js";

import { supabase } from "@/lib/supabaseClient";

/**
 * SPEC-DOCCONSENT-2 — acceptance metadata carried into Supabase signUp.
 * The auth.users trigger reads `raw_user_meta_data->'terms_acceptance'`
 * and writes the two `public.document_consents` rows from the SERVER
 * text — but we carry the user's CLIENT-side versions + hashes too so a
 * later audit can detect "the bytes the user saw differed from what we
 * stored". `accepted` is a TS literal `true` so the value is provably
 * affirmative at the call site (and at every test boundary).
 */
export interface TermsAcceptanceMetadata {
  accepted: true;
  tos_version: string;
  tos_hash: string;
  privacy_version: string;
  privacy_hash: string;
  user_agent: string;
  source: "ifrs16";
}

interface AuthContextValue {
  user: User | null;
  session: Session | null;
  loading: boolean;
  signIn: (email: string, password: string) => Promise<void>;
  signUp: (
    email: string,
    password: string,
    fullName: string,
    termsAcceptance?: TermsAcceptanceMetadata,
  ) => Promise<{ needsConfirmation: boolean }>;
  signOut: () => Promise<void>;
  /**
   * SPEC-27 — sends a password-reset email containing a link back to
   * `/reset-password` on the same origin. Never reveal whether the
   * address is registered: callers should show an identical neutral
   * toast on both the resolved and rejected paths.
   */
  requestPasswordReset: (email: string) => Promise<void>;
  /**
   * SPEC-27 — updates the password on the active session. Used by
   * `/reset-password` once supabase-js has consumed the recovery hash
   * and `onAuthStateChange` has issued a `PASSWORD_RECOVERY` session.
   */
  updatePassword: (newPassword: string) => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

/**
 * SPEC-16 (FAZ 2, part 1) — session context backed by Supabase Auth.
 *
 * Bootstraps from the persisted session on mount and subscribes to
 * `onAuthStateChange` so login/logout/token-refresh flows propagate
 * automatically. Route guarding is intentionally NOT part of this spec
 * (lands in SPEC-17); the app remains openly reachable until then.
 */
export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    supabase.auth
      .getSession()
      .then(({ data }) => {
        if (cancelled) return;
        setSession(data.session);
        setUser(data.session?.user ?? null);
        setLoading(false);
      })
      // Finding E8 — getSession() is the only place `loading` is cleared on
      // boot. Without a catch, a rejected promise (corrupt persisted session,
      // a storage throw, a boot token-refresh failure) leaves `loading` true
      // forever and the auth guards render a blank splash with no recovery.
      // Treat a boot failure as "not authenticated" and fail open to the login
      // flow; onAuthStateChange will still correct the state if a session
      // later materialises.
      .catch(() => {
        if (cancelled) return;
        setSession(null);
        setUser(null);
        setLoading(false);
      });
    const { data: sub } = supabase.auth.onAuthStateChange((_event, sess) => {
      setSession(sess);
      setUser(sess?.user ?? null);
    });
    return () => {
      cancelled = true;
      sub.subscription.unsubscribe();
    };
  }, []);

  const signIn = async (email: string, password: string): Promise<void> => {
    const { error } = await supabase.auth.signInWithPassword({
      email,
      password,
    });
    if (error) throw new Error(error.message);
  };

  const signUp = async (
    email: string,
    password: string,
    fullName: string,
    termsAcceptance?: TermsAcceptanceMetadata,
  ): Promise<{ needsConfirmation: boolean }> => {
    const { data, error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        // SPEC-DOCCONSENT-2 — when the caller provides a
        // termsAcceptance payload (signup form with the agree checkbox
        // checked), Supabase persists it on auth.users
        // raw_user_meta_data so the existing signup trigger can fan it
        // into public.document_consents from the SERVER text. When
        // absent (existing 3-arg call sites — e.g. older AuthProvider
        // tests), only full_name is forwarded, preserving back-compat.
        data: {
          full_name: fullName,
          ...(termsAcceptance ? { terms_acceptance: termsAcceptance } : {}),
        },
        // SPEC-28 — when Supabase "Confirm email" is on, the
        // verification link lands the user on `/login` on the same
        // origin. LoginRoute auto-bounces to `/` if supabase-js
        // happened to establish a session from the link; otherwise the
        // user sees the login form and can sign in.
        emailRedirectTo: `${window.location.origin}/login`,
      },
    });
    if (error) throw new Error(error.message);
    // No session yet ⇒ email confirmation is enabled in Supabase Auth
    // settings and the user must click the verification link before
    // they can sign in.
    return { needsConfirmation: data.session == null };
  };

  const signOut = async (): Promise<void> => {
    const { error } = await supabase.auth.signOut();
    if (error) throw new Error(error.message);
  };

  const requestPasswordReset = async (email: string): Promise<void> => {
    const { error } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: `${window.location.origin}/reset-password`,
    });
    if (error) throw new Error(error.message);
  };

  const updatePassword = async (newPassword: string): Promise<void> => {
    const { error } = await supabase.auth.updateUser({
      password: newPassword,
    });
    if (error) throw new Error(error.message);
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        session,
        loading,
        signIn,
        signUp,
        signOut,
        requestPasswordReset,
        updatePassword,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error("useAuth must be used within <AuthProvider>");
  }
  return ctx;
}
